import React, { useEffect, useRef } from 'react';
import { BlockNoteView } from '@blocknote/mantine';
import { Box, type BoxProps } from '@mui/material';

import { useBlockNoteEditor } from '../../hooks/useBlockNoteEditor';
import { useBlockNoteSelectionAnalytics } from '../../hooks/useBlockNoteSelectionAnalytics';
import { useMarkdownSectionAnalytics } from '../../hooks/useMarkdownSectionAnalytics';
import {
  type NoteTelemetryEventType,
  trackNoteTelemetry,
} from '../../utils/analytics/noteTelemetry';
import { copyFromEditor } from '../../utils/blocknote/copy';
import type { DiffOperation } from '../../utils/blocknote/diffHighlightPlugin';
import type { OnBracketClick } from '../BlockNoteBracketHighlightPlugin';

import '@blocknote/core/fonts/inter.css';
import '@blocknote/mantine/style.css';
import '../../styles/blockNoteStyles.css';

interface EditorViewProps extends Omit<BoxProps, 'onChange'> {
  /**
   * Markdown content to display/edit
   */
  markdown: string;

  /**
   * Whether the editor is editable
   */
  editable: boolean;

  /**
   * Callback when markdown content changes (only fired when editable)
   */
  onMarkdownChange?: (markdown: string) => void;

  /**
   * Callback when editor loses focus (blur event)
   * Only fires when editable and focus moves outside the editor container
   */
  onBlur?: () => void;

  /**
   * Callback when editor gains focus
   * Fires when focus moves into the editor container
   */
  onFocus?: () => void;

  /**
   * Whether to enable the bracket highlight plugin
   */
  enableBracketHighlight?: boolean;

  /**
   * Whether to enable the misspelling plugin
   */
  enableMisspelling?: boolean;

  /**
   * Array of misspelled words to highlight (required if enableMisspelling is true)
   */
  misspelledWords?: string[];

  /**
   * Callback when a misspelled word is clicked
   */
  onWordClick?: (word: string) => void;

  /**
   * Array of trouble words with correct spellings
   */
  troubleWords?: Array<{ correct_spelling: string; misspellings: string[] }>;

  /**
   * Whether to enable the diff highlight plugin
   * Shows inline additions/deletions on hover
   */
  enableDiff?: boolean;

  /**
   * Array of diff operations to highlight (required if enableDiff is true)
   * Format: [[operation, text], ...] where operation is -1 (delete), 0 (unchanged), 1 (insert)
   */
  diff?: DiffOperation[];

  /**
   * Callback when editor is ready
   */
  onEditorReady?: (editor: any) => void;

  /**
   * Callback to receive editor container DOM reference
   * Useful for position tracking and DOM queries
   */
  onEditorContainerRef?: (container: HTMLDivElement | null) => void;

  /**
   * Callback when a bracket highlight is clicked
   */
  onBracketClick?: OnBracketClick;

  /**
   * Unique style ID to avoid style conflicts
   */
  styleId?: string;

  /**
   * Whether to show the side menu (drag handle and + button)
   * @default false
   */
  sideMenu?: boolean;

  /**
   * Whether to show the formatting toolbar on text selection
   * Set to false to disable the default popup toolbar
   * @default true
   */
  formattingToolbar?: boolean;

  /**
   * Children to render inside BlockNoteView context
   * Useful for custom toolbars and menus that need access to the editor context
   */
  children?: React.ReactNode;

  /**
   * Additional style props for the container
   */
  containerStyle?: React.CSSProperties;

  /**
   * Analytics configuration
   */
  analytics?: {
    enabled?: boolean;
    docId?: string;
    getDocVersion?: () => string;
    metadata?: {
      module_id?: string;
      section_id?: string;
      note_id?: string;
      encounter_id?: string;
    };
    sections?: {
      enabled?: boolean;
      headingLevels?: number[];
    };
    onEvent?: (type: NoteTelemetryEventType, payload: any) => void;
  };
}

/**
 * Shared BlockNote editor view component
 * Provides a consistent editing experience across the application
 */
export const EditorView: React.FC<EditorViewProps> = ({
  markdown,
  editable,
  onMarkdownChange,
  onBlur,
  onFocus,
  enableBracketHighlight = false,
  enableMisspelling = false,
  misspelledWords = [],
  onWordClick,
  troubleWords,
  enableDiff = false,
  diff,
  onEditorReady,
  onEditorContainerRef,
  onBracketClick,
  styleId,
  sideMenu = true,
  formattingToolbar = true,
  children,
  containerStyle,
  analytics,
  ...boxProps
}) => {
  // Ensure markdown is always a string, defaulting to empty string if undefined
  const safeMarkdown = markdown ?? '';

  const { editor, isReady } = useBlockNoteEditor({
    initialMarkdown: safeMarkdown,
    enableBracketHighlight,
    onBracketClick: enableBracketHighlight ? onBracketClick : undefined,
    enableMisspelling,
    misspelledWords,
    onWordClick,
    troubleWords,
    enableDiff,
    diff,
    onEditorReady,
    styleId,
  });

  // Track last markdown to avoid unnecessary updates
  const lastMarkdownRef = useRef<string>('');

  // Track document version for analytics (increments when markdown changes)
  const docVersionRef = useRef<number>(0);
  useEffect(() => {
    if (lastMarkdownRef.current !== markdown) {
      docVersionRef.current += 1;
    }
  }, [markdown]);

  // Editor root ref for analytics
  const editorRootRef = useRef<HTMLDivElement>(null);

  // Expose editor container ref to parent
  useEffect(() => {
    if (onEditorContainerRef) {
      onEditorContainerRef(editorRootRef.current);
    }
  }, [onEditorContainerRef]);

  // Set up blur detection using focusout event on container
  useEffect(() => {
    const container = editorRootRef.current;
    if (!container || !onBlur || !editable) return;

    const handleFocusOut = (e: FocusEvent) => {
      // Check if focus moved outside the editor container
      if (!container.contains(e.relatedTarget as Node)) {
        onBlur();
      }
    };

    container.addEventListener('focusout', handleFocusOut);

    return () => {
      container.removeEventListener('focusout', handleFocusOut);
    };
    // isReady is needed because the component returns null until ready,
    // so the ref isn't set until isReady becomes true
  }, [onBlur, editable, isReady]);

  // Set up focus detection using focusin event on container
  useEffect(() => {
    const container = editorRootRef.current;
    if (!container || !onFocus || !editable) return;

    const handleFocusIn = () => {
      // Fire onFocus whenever any element inside the container receives focus
      onFocus();
    };

    container.addEventListener('focusin', handleFocusIn);

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
    };
    // isReady is needed because the component returns null until ready,
    // so the ref isn't set until isReady becomes true
  }, [onFocus, editable, isReady]);

  // Wire up analytics if enabled
  const analyticsEnabled = analytics?.enabled ?? false;
  const analyticsDocId = analytics?.docId ?? styleId ?? 'blocknote:unknown';
  const getDocVersion = analytics?.getDocVersion ?? (() => docVersionRef.current.toString());
  const analyticsOnEvent = analytics?.onEvent ?? trackNoteTelemetry;

  useBlockNoteSelectionAnalytics(analyticsEnabled ? editor : undefined, editorRootRef, {
    docId: analyticsDocId,
    getDocVersion,
    metadata: analytics?.metadata,
    sections: analytics?.sections,
    onEvent: analyticsOnEvent,
  });

  // Section-level analytics (for markdown with headings)
  const sectionsEnabled = analyticsEnabled && analytics?.sections?.enabled === true;
  useMarkdownSectionAnalytics(
    sectionsEnabled ? editorRootRef : { current: null },
    sectionsEnabled
      ? {
          docId: analyticsDocId,
          getDocVersion,
          onEvent: analyticsOnEvent,
          headingLevels: analytics?.sections?.headingLevels,
          metadata: analytics?.metadata,
        }
      : {
          docId: analyticsDocId,
          getDocVersion,
          onEvent: analyticsOnEvent,
          metadata: analytics?.metadata,
        },
  );

  // Update editor content when markdown prop changes
  // CRITICAL: Only sync when NOT editable to prevent cursor jumps while typing
  // When editable, the editor content is the source of truth and should not be overwritten
  useEffect(() => {
    if (!isReady || !editor || editable) return; // Skip sync when editable

    // Skip if markdown hasn't actually changed
    if (lastMarkdownRef.current === safeMarkdown) return;

    const updateContent = async () => {
      try {
        const { extractMarkdownFromCodeBlocks, normalizeMarkdownInput, normalizeMarkdownNewlines } =
          await import('../../utils/utils');
        let processedText = normalizeMarkdownInput(safeMarkdown);
        processedText = extractMarkdownFromCodeBlocks(processedText);
        // Normalize newlines to preserve single line breaks as paragraph breaks
        processedText = normalizeMarkdownNewlines(processedText);
        if (processedText) {
          const blocks = await editor.tryParseMarkdownToBlocks(processedText);
          // Ensure blocks array doesn't contain undefined elements
          const validBlocks = blocks.filter(block => block != null);
          // Ensure we have at least one block
          if (validBlocks.length > 0) {
            editor.replaceBlocks(editor.document, validBlocks);
          } else {
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
          }
          lastMarkdownRef.current = safeMarkdown;
        } else {
          // Ensure editor has at least one empty block
          editor.replaceBlocks(editor.document, [
            {
              type: 'paragraph',
              content: '',
            } as any,
          ]);
          lastMarkdownRef.current = '';
        }
      } catch (err) {
        console.error('Error updating editor content:', err);
        // Fallback: ensure editor has at least one valid block
        try {
          editor.replaceBlocks(editor.document, [
            {
              type: 'paragraph',
              content: '',
            } as any,
          ]);
          lastMarkdownRef.current = '';
        } catch (fallbackErr) {
          console.error('Error in fallback editor update:', fallbackErr);
        }
      }
    };

    updateContent();
  }, [editor, safeMarkdown, isReady, editable]);

  // Wire up onMarkdownChange callback when editor content changes (only when editable)
  // PERFORMANCE: Debounced to prevent blocking UI thread on every keystroke
  useEffect(() => {
    if (!isReady || !editor || !editable || !onMarkdownChange) return;

    let debounceTimer: NodeJS.Timeout | null = null;

    const unsubscribe = editor.onChange(() => {
      // Clear existing timer to reset debounce
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Debounce markdown conversion (300ms) to avoid blocking on every keystroke
      debounceTimer = setTimeout(async () => {
        try {
          const markdown = await editor.blocksToMarkdownLossy(editor.document);
          onMarkdownChange(markdown);
        } catch (err) {
          console.error('Error converting blocks to markdown in onChange:', err);
        }
      }, 300);
    });

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [editor, isReady, editable, onMarkdownChange]);

  // Track previous editable state to detect transitions
  const prevEditableRef = useRef<boolean>(editable);

  // Sync markdown when transitioning between editable states
  // - From editable to non-editable: ensures editor reflects latest prop value
  // - From non-editable to editable: ensures final streamed content is rendered before editing
  useEffect(() => {
    if (!isReady || !editor) return;

    // Detect transitions
    const wasEditable = prevEditableRef.current;
    const isNowEditable = editable;
    const isTransition = wasEditable !== isNowEditable;

    // Only sync on transitions, and only if content differs
    if (isTransition && lastMarkdownRef.current !== safeMarkdown) {
      const syncOnModeChange = async () => {
        try {
          const {
            extractMarkdownFromCodeBlocks,
            normalizeMarkdownInput,
            normalizeMarkdownNewlines,
          } = await import('../../utils/utils');
          let processedText = normalizeMarkdownInput(safeMarkdown);
          processedText = extractMarkdownFromCodeBlocks(processedText);
          // Normalize newlines to preserve single line breaks as paragraph breaks
          processedText = normalizeMarkdownNewlines(processedText);
          if (processedText) {
            const blocks = await editor.tryParseMarkdownToBlocks(processedText);
            // Ensure blocks array doesn't contain undefined elements
            const validBlocks = blocks.filter(block => block != null);
            // Ensure we have at least one block
            if (validBlocks.length > 0) {
              editor.replaceBlocks(editor.document, validBlocks);
            } else {
              editor.replaceBlocks(editor.document, [
                {
                  type: 'paragraph',
                  content: '',
                } as any,
              ]);
            }
            lastMarkdownRef.current = safeMarkdown;
          } else {
            // Ensure editor has at least one empty block
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
            lastMarkdownRef.current = '';
          }
        } catch (err) {
          console.error('Error syncing editor on mode change:', err);
          // Fallback: ensure editor has at least one valid block
          try {
            editor.replaceBlocks(editor.document, [
              {
                type: 'paragraph',
                content: '',
              } as any,
            ]);
            lastMarkdownRef.current = '';
          } catch (fallbackErr) {
            console.error('Error in fallback editor sync:', fallbackErr);
          }
        }
      };

      syncOnModeChange();
    }

    // Update ref for next comparison
    prevEditableRef.current = editable;
  }, [editor, editable, isReady, safeMarkdown]);

  if (!isReady) {
    return null; // Or return a loading spinner if desired
  }

  return (
    <>
      <Box
        {...boxProps}
        sx={{
          ...(boxProps.sx as any),
          ...containerStyle,
        }}
      >
        <div
          ref={editorRootRef}
          style={{
            position: 'relative',
            borderRadius: '0.75rem',
          }}
        >
          <BlockNoteView
            editor={editor}
            editable={editable}
            theme="light"
            sideMenu={sideMenu}
            formattingToolbar={formattingToolbar}
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              margin: 0,
            }}
          >
            {children}
          </BlockNoteView>
        </div>
      </Box>
    </>
  );
};

/**
 * Utility function to copy content from an EditorView's editor
 * Exported for use in parent components that need copy functionality
 */
export const copyEditorContent = async (
  editor: any,
  fallbackMarkdown?: string,
  includeHtml: boolean = true,
): Promise<boolean> => {
  return copyFromEditor(editor, fallbackMarkdown, includeHtml);
};
